import React, { useEffect, useRef, useState } from "react";
import { getAccessToken } from "../../api/client";

/**
 * Photo bytes sit behind a bearer token, and a plain <img src> cannot send an
 * Authorization header. This fetches the image, hands the browser an object
 * URL, and revokes it on unmount so the blobs do not accumulate as the user
 * pages through a large library.
 */
export default function AuthImage({ src, alt = "", className, onLoaded, ...rest }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setUrl(null);

    const token = getAccessToken();
    fetch(src, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        urlRef.current = objectUrl;
        setUrl(objectUrl);
        if (onLoaded) onLoaded();
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
    // onLoaded is intentionally not a dependency: callers pass inline
    // functions, and re-fetching the image on every render would defeat this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (failed) return <div className={`authimg authimg-failed ${className || ""}`} />;
  if (!url) return <div className={`authimg authimg-loading ${className || ""}`} />;
  return <img className={className} src={url} alt={alt} {...rest} />;
}

/** Fetches a photo as a data URL, for handing to the card renderer. */
export async function fetchPhotoDataUrl(src) {
  const token = getAccessToken();
  const res = await fetch(src, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) throw new Error("Could not load that photo.");
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read that photo."));
    reader.readAsDataURL(blob);
  });
}
